import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Alert,
    CircularProgress,
    TextField
} from '@mui/material';
import axios from '../../utils/axios';

const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [email, setEmail] = useState('');
    const [showResendForm, setShowResendForm] = useState(false);
    const [debugInfo, setDebugInfo] = useState(null);

    useEffect(() => {
        const verifyEmail = async () => {
            // Get token and userId from URL parameters
            const searchParams = new URLSearchParams(location.search);
            const token = searchParams.get('token');
            const userId = searchParams.get('userId');

            // Store some debug info for development
            if (process.env.NODE_ENV === 'development') {
                setDebugInfo({
                    fullUrl: window.location.href,
                    searchParams: location.search,
                    token,
                    userId
                });
            }

            if (!token || !userId) {
                setError('Invalid verification link - missing token or userId');
                setLoading(false);
                return;
            }

            try {
                console.log(`Attempting to verify email with userId: ${userId} and token: ${token.substring(0, 10)}...`);
                const response = await axios.get(`/api/users/verify-email/${userId}/${token}`);
                setSuccess(response.data.message || 'Email verified successfully. You can now login.');
            } catch (err) {
                console.error('Verification error:', err);
                const errorMsg = err.response?.data?.message || 'Email verification failed';
                setError(errorMsg);
                setShowResendForm(true);
            } finally {
                setLoading(false);
            }
        };

        verifyEmail();
    }, [location.search]);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    };

    const handleResendVerification = async (e) => {
        if (e) e.preventDefault();
        
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post('/api/users/resend-verification', { email });
            setSuccess(response.data.message || 'Verification email has been sent. Please check your inbox.');
            setError('');
            setShowResendForm(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend verification email');
            setSuccess('');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = () => {
        navigate('/login');
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Email Verification
                </Typography>

                {loading && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Debug information in development mode */}
                {process.env.NODE_ENV === 'development' && debugInfo && !success && (
                    <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="subtitle2">Debug Info:</Typography>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                            {JSON.stringify(debugInfo, null, 2)}
                        </pre>
                    </Alert>
                )}

                {!loading && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {success ? (
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleLogin}
                            >
                                Go to Login
                            </Button>
                        ) : showResendForm ? (
                            <form onSubmit={handleResendVerification}>
                                <Typography variant="body1" gutterBottom>
                                    Enter your email address to resend the verification link:
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    margin="normal"
                                    required
                                />
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        Resend Verification Email
                                    </Button>
                                </Box>
                            </form>
                        ) : (
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={() => setShowResendForm(true)}
                                disabled={loading}
                            >
                                Need a New Verification Link?
                            </Button>
                        )}
                        
                        {!success && (
                            <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={handleLogin}
                            >
                                Back to Login
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default VerifyEmail; 